import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
	ArrowDownToLine,
	ArrowUpFromLine,
	FileClock,
	Scale,
	WalletCards,
	type LucideIcon,
} from "lucide-react";
import { toast } from "sonner";

import { orpc } from "@/client";
import { Button } from "@/components/ui/button";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog";
import { FinanceiroLabels } from "@/lib/labels/financeiro";
import { cn } from "@/lib/utils";
import type { CashMovement } from "@/types/financeiro";

import { StatePill } from "./primitives";
import type { FinanceModal } from "./types";

const MOVEMENT: Record<CashMovement["kind"], { label: string; icon: LucideIcon }> = {
	opening: { label: "Abertura", icon: WalletCards },
	deposit: { label: "Depósito bancário", icon: ArrowUpFromLine },
	withdrawal: { label: "Retirada", icon: ArrowDownToLine },
	expense: { label: "Conta paga em espécie", icon: FileClock },
	adjustment: { label: "Ajuste de contagem", icon: Scale },
};

export function CashMovements({
	movements,
	onModal,
}: {
	movements: CashMovement[];
	onModal: (modal: FinanceModal) => void;
}) {
	if (movements.length === 0) {
		return null;
	}

	return (
		<ul className="mt-3 min-w-0 divide-y divide-border @container">
			{movements.map((movement) => {
				const definition = MOVEMENT[movement.kind];
				const Icon = definition.icon;
				const signed = movement.direction === "in" ? movement.amount_cents : -movement.amount_cents;
				const title =
					movement.kind === "expense" && movement.bill_description !== null
						? movement.bill_description
						: definition.label;
				const depositEvidence = movement.bank_evidence_valid
					? { label: "Vinculado", tone: "green" as const }
					: { label: "Comprovante inválido", tone: "coral" as const };

				return (
					<li
						key={movement.id}
						className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-3 py-4 @3xl:grid-cols-[minmax(0,1fr)_9rem_minmax(16rem,auto)] @3xl:items-center"
					>
						<div className="flex min-w-0 items-start gap-3">
							<Icon className="mt-0.5 size-4 shrink-0 text-primary" />
							<div className="min-w-0 flex-1">
								<p className="break-words text-sm font-semibold text-foreground">{title}</p>
								{movement.description !== null && (
									<p className="mt-1 break-words text-xs text-muted-foreground">
										{movement.description}
									</p>
								)}
								<p className="mt-1 text-xs text-muted-foreground">
									Registrado por {movement.author}
								</p>
							</div>
						</div>
						<p
							className={cn(
								"text-right font-mono text-sm font-semibold",
								signed > 0 ? "text-success" : "text-foreground",
							)}
						>
							{FinanceiroLabels.signed(signed)}
						</p>
						<div className="col-span-2 flex min-w-0 flex-wrap items-center gap-2 empty:hidden @3xl:col-span-1 @3xl:justify-end">
							{movement.cancelled_at !== null && <StatePill label="Cancelado" tone="muted" />}
							{movement.cancelled_at === null &&
								movement.kind === "deposit" &&
								movement.bank_transaction_id !== null && (
									<StatePill label={depositEvidence.label} tone={depositEvidence.tone} />
								)}
							{movement.cancelled_at === null && movement.kind === "deposit" && (
								<Button
									size="sm"
									variant="ghost"
									onClick={() =>
										onModal({
											kind: "cash-deposit-evidence",
											id: movement.id,
											linked: movement.bank_transaction_id !== null,
										})
									}
								>
									{movement.bank_transaction_id === null && "Vincular depósito"}
									{movement.bank_transaction_id !== null && "Alterar vínculo"}
								</Button>
							)}
							{movement.cancelled_at === null &&
								(movement.kind === "deposit" ||
									movement.kind === "withdrawal" ||
									movement.kind === "expense") && (
									<CashMovementCancelButton
										id={movement.id}
										kind={movement.kind}
										amountCents={movement.amount_cents}
									/>
								)}
						</div>
					</li>
				);
			})}
		</ul>
	);
}

function CashMovementCancelButton({
	id,
	kind,
	amountCents,
}: {
	id: number;
	kind: Extract<CashMovement["kind"], "deposit" | "withdrawal" | "expense">;
	amountCents: number;
}) {
	const queryClient = useQueryClient();
	const { mutate, isPending } = useMutation(
		orpc.financeiro.cash.cancel.mutationOptions({
			onSuccess: async () => {
				await queryClient.invalidateQueries({ queryKey: orpc.financeiro.key() });
				toast.success(kind === "expense" ? "Pagamento cancelado" : "Movimento cancelado");
			},
			onError: () => toast.error("Não foi possível cancelar o movimento"),
		}),
	);

	return (
		<ConfirmationDialog
			slot="cancel-cash-movement"
			tone="destructive"
			title={kind === "expense" ? "Cancelar pagamento?" : "Cancelar movimento?"}
			description={
				kind === "expense"
					? `A conta de ${FinanceiroLabels.money(amountCents)} voltará a ficar em aberto.`
					: `${FinanceiroLabels.money(amountCents)} deixará de compor o saldo do caixa.`
			}
			pendingLabel="Cancelando"
			isPending={isPending}
			onConfirm={() => mutate({ id })}
			className="px-2 text-xs text-muted-foreground hover:text-destructive"
		>
			Cancelar
		</ConfirmationDialog>
	);
}
